import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './WritingArticlePage.css';

type ArticleLanguage = 'zh' | 'en';

type ArticleCopy = {
  backLabel: string;
  eyebrow: string;
  title: string;
  meta: string;
  paragraphs: string[];
};

const ARTICLE_SLUGS = [
  'ai-why-still-try',
  'milk-foam-not-like-this',
  'cat-fur-in-my-pocket',
] as const;

type ArticleSlug = typeof ARTICLE_SLUGS[number];

const ARTICLE_COPY: Record<ArticleSlug, Record<ArticleLanguage, ArticleCopy>> = {
  'ai-why-still-try': {
    zh: {
      backLabel: 'back to writing',
      eyebrow: 'AI 随笔',
      title: 'AI 这么强，人还努力干嘛？',
      meta: 'Essay · Mia Miao',
      paragraphs: [
        'AI 出现以后，大家都在这样问。',
        '一段文字，一个页面，一个工具，把一个想法变成可以运行的东西，现在都比以前容易太多。',
        '我男朋友学 IT。以前他最大的成就感，是写自己的软件。可最近，让他最苦恼的也正是这件事——当一个小软件都可以被 AI 很快做出来，创作本身好像没有以前那么值得骄傲了。',
        '是啊，如果最后呈现出来的东西，AI 可以做，别人也可以借助 AI 做，那为什么这个创造非你不可？如果今天要花三五年才能练出来的能力，未来别人一两天就能做到差不多的结果，那这三五年算什么？如果所有结果都可以被加速，那慢慢来这件事，本身还值不值得？',
        '人很容易这样。一旦某件事可以被计算，就以为它可以被计算得清清楚楚。',
        '这段时间，我开始调整自己的身体状态。',
        '刚开始的时候，我很认真地计算卡路里，比如兼职端盘子站十个小时，比认真训练一个小时更有用。因为如果只按热量来算，前者消耗的数字看起来确实更大。',
        '可是后来我发现，我不是只想让体重秤上的数字降下来。如果只是为了制造热量差，兼职端盘子站十个小时当然也算努力，甚至可能消耗得更多。可认真训练的那一个小时，不只是为了消耗。它让我心肺更稳，让身体更有力量，让线条一点点出来，也让我在想停下来的时候，学会再撑一下。',
        '端盘子会让我累，但训练会让我变强。',
        '很多事也是这样，如果只看最后的结果，AI 的确让很多东西来得太容易了。一个作品，一个方案，一个页面，一段代码，过去可能代表一个人很长时间的练习。现在，它有时候只代表一次不错的提问，和几轮顺利的修改。',
        '可是人做一件事，真的只是在要那个结果吗？',
        '有时候不是。',
        '你写下一句话，才发现自己其实没有想清楚；你做一个东西，才发现问题不在怎么做，而在一开始有没有问对问题；你改来改去，才发现真正难的不是把它完成，而是知道什么值得留下，什么应该删掉。',
        '这些过程，AI 可以帮忙，但它不能替你变成那个经历过的人。',
        '所以也许 AI 改变的，是结果的稀缺性，不是过程的意义。结果像体重秤上的数字，很清楚，也很容易比较；过程像身体里慢慢长出来的力气，不一定立刻看得见，但它会改变你下一次怎么站、怎么走、怎么判断。',
        'AI 让很多东西来得更快。',
        '但一个人有没有变得更清楚、更敏锐、更诚实，还是只能慢慢练。',
        '调整身体状态不是为了证明今天消耗了多少卡路里，做一件事也不是为了证明世界上又多了一个成品。',
        '有些东西，机器可以生成，有些东西，只能在人身上发生。',
      ],
    },
    en: {
      backLabel: 'back to writing',
      eyebrow: 'AI notes',
      title: 'If AI Is So Powerful, Why Should We Still Try?',
      meta: 'Essay · Mia Miao',
      paragraphs: [
        'Since AI appeared, everyone has been asking this.',
        'A paragraph, a page, a tool. Turning an idea into something that can run is now much easier than it used to be.',
        'My boyfriend studies IT. Before, his greatest sense of achievement came from writing his own software. But recently, the thing troubling him most is exactly that: when even a small piece of software can be made quickly with AI, the act of creating no longer seems as worth being proud of as it once did.',
        'And yes, if the final thing can be made by AI, and other people can also make it with AI, why does this creation have to come from you? If an ability that used to take three to five years to develop can be matched by someone else in a day or two, what do those three to five years mean? If every result can be accelerated, is taking things slowly still worth anything?',
        'People fall into this very easily. Once something can be calculated, we start to think it can be calculated completely.',
        'Recently, I started adjusting my physical condition.',
        'At first, I calculated calories very carefully. For example, working a part-time shift carrying plates and standing for ten hours seemed more useful than one hour of serious training. If you only count calories, the first number does look bigger.',
        'But later I realized I did not only want the number on the scale to go down. If the goal is just to create a calorie deficit, then ten hours of serving tables certainly counts as effort, and may even burn more. But that one hour of real training is not only about burning calories. It steadies my heart and lungs, gives my body more strength, lets shape appear little by little, and teaches me, when I want to stop, to hold on a little longer.',
        'Serving tables makes me tired. Training makes me stronger.',
        'Many things are like this. If we only look at the final result, AI really does make many things arrive too easily. A piece of work, a proposal, a page, a piece of code. In the past, these might have represented a person’s long practice. Now, sometimes they only represent a good prompt and a few smooth rounds of revision.',
        'But when a person does something, are they really only asking for that result?',
        'Sometimes, no.',
        'You write down a sentence and only then realize you have not actually thought it through. You make something and only then discover that the problem is not how to make it, but whether you asked the right question at the beginning. You revise again and again and only then realize that the truly difficult part is not finishing it, but knowing what is worth keeping and what should be deleted.',
        'AI can help with these processes, but it cannot become the person who has lived through them for you.',
        'So perhaps what AI changes is the scarcity of results, not the meaning of the process. Results are like the number on a scale: clear, visible, and easy to compare. Process is like strength slowly growing inside the body. You may not see it immediately, but it changes how you stand, how you walk, and how you judge the next time.',
        'AI makes many things arrive faster.',
        'But whether a person becomes clearer, sharper, and more honest is still something that can only be trained slowly.',
        'Adjusting your physical condition is not about proving how many calories you burned today. Doing something is not about proving that the world has one more finished product.',
        'Some things can be generated by machines. Some things can only happen inside a human being.',
      ],
    },
  },
  'milk-foam-not-like-this': {
    zh: {
      backLabel: 'back to writing',
      eyebrow: '咖啡店观察',
      title: '奶泡不是这样打的吗？',
      meta: 'Note · Mia Miao',
      paragraphs: [
        '昨天在一个老年社区的 Cafe 顶班，用到一台很厉害的咖啡机，叫 WMF Espresso Next。',
        '手柄扣上去，选好 shot 数，它自己磨豆、落粉、压粉、萃取。奶缸放到奶棒下面，选好咖啡类型，它自己进气、加热、打泡，停在刚好的温度。',
        '真正一键出杯的全自动咖啡机其实到处都是。酒店大堂、公司 pantry、自助早餐区，按一下 latte，按一下 flat white，一整杯就出来了。',
        '但人好像还是愿意去柜台前排队。听听奶泡的声音，看杯子被递来递去，跟咖啡师聊几句闲天。也许一杯咖啡让人愿意等，不只因为味道，也因为它曾经从一个人的手里经过。',
        '这台机器妙就妙在，它没有把人拿走。',
        '手柄还是我扣的，杯子还是我放的，牛奶还是我倒的，最后那朵花，也还是我拉出来的。那些最难控制的部分，它已经帮我控制好了。',
        '粉量不会忽多忽少，压粉不会一边轻一边重，奶泡不会太厚，也不会太稀。杯子一斜，手腕一抬，花就出来了。',
        '舒服到我差点忘了，为什么会这么舒服。',
        '真正让我疑惑的，是那根一直埋在牛奶里的奶棒。',
        '我一直以为，打奶泡要在牛奶表面进气。蒸汽棒靠近液面，听到细细的声音，空气才会进去。前期浅一点，后期深一点。浅的时候打泡，深的时候融合。',
        '好像咖啡本来就该这么做。',
        '所以当我看到那根蒸汽棒一直在牛奶下面，却能打出不同厚度的奶泡时，我第一反应是：这怎么可能？',
        '后来才明白，不是进气只能发生在表面。',
        '是人只能在表面控制进气。',
        '我们移动奶缸，是因为手上没有别的开关。机器不需要追着液面找角度。它在里面决定什么时候进气，进多少，什么时候停。',
        '它没有夺走我的参与感。它只是把那些不稳定，悄悄藏起来了。',
        '我那时候才意识到，我从来没有真的想过“进气”这件事本身。',
        '因为我们一直在牛奶表面进气，就以为只有牛奶表面才能进气。',
        '可也许很多常识，只是旧工具留下来的形状。',
        '原来进气不一定要在表面。',
      ],
    },
    en: {
      backLabel: 'back to writing',
      eyebrow: 'Cafe notes',
      title: "Isn't This How You Steam Milk?",
      meta: 'Note · Mia Miao',
      paragraphs: [
        'Yesterday I was covering a shift at a cafe in a retirement community, and I got to use a very impressive coffee machine called the WMF Espresso Next.',
        'Lock in the portafilter, choose the number of shots, and it grinds, doses, tamps, and extracts by itself. Put the milk jug under the steam wand, choose the coffee type, and it aerates, heats, textures the milk, and stops at just the right temperature.',
        'Truly one-touch automatic coffee machines are everywhere: hotel lobbies, company pantries, self-serve breakfast areas. Press latte, press flat white, and the whole cup comes out.',
        'But people still seem willing to queue at the counter. To listen to the sound of milk steaming, watch cups being passed back and forth, and chat with the barista for a moment. Maybe a cup of coffee is worth waiting for not only because of its taste, but because it has passed through someone’s hands.',
        'The clever thing about this machine is that it does not remove the person.',
        'I still lock in the portafilter, place the cup, and pour the milk. The final flower is still poured by me. The parts that are hardest to control have already been controlled for me.',
        'The dose does not swing high or low, the tamp is not uneven, and the milk foam is neither too thick nor too thin. Tilt the cup, lift the wrist, and the pattern appears.',
        'It felt so comfortable that I almost forgot why it felt so comfortable.',
        'What really puzzled me was the steam wand that stayed buried in the milk the whole time.',
        'I had always thought you had to aerate milk at the surface. The steam wand sits near the liquid line, you hear that fine little sound, and only then does air enter. A little shallower at the beginning, deeper later. Aerate when shallow, integrate when deep.',
        'It felt like this was simply how coffee was supposed to be made.',
        'So when I saw a steam wand sitting under the milk the entire time, yet still making foam with different textures, my first reaction was: how is that possible?',
        'Only later did I understand: it is not that aeration can only happen at the surface.',
        'It is that humans can only control aeration at the surface.',
        'We move the milk jug because our hands have no other switch. The machine does not need to chase the liquid line for the right angle. Inside, it decides when to introduce air, how much to introduce, and when to stop.',
        'It did not take away my sense of participation. It simply hid the unstable parts away.',
        'That was when I realized I had never really thought about aeration itself.',
        'Because we have always introduced air at the surface of the milk, we start to believe air can only be introduced at the surface.',
        'But maybe many forms of common sense are just the shape left behind by old tools.',
        'It turns out aeration does not have to happen at the surface.',
      ],
    },
  },
  'cat-fur-in-my-pocket': {
    zh: {
      backLabel: 'back to writing',
      eyebrow: '咖啡店日记',
      title: '我从口袋里摸出小猫的毛',
      meta: 'Diary · Mia Miao',
      paragraphs: [
        '咖啡师的一天又开始了。',
        '手还没擦干，就又要去拿杯子。盘子刚放下，又有人来点单了。有人在身后叫我，有人在前面等我，还有人经过时顺口交代一句什么。',
        '一整天都像这样，被声音推着走。',
        '收完盘子，丢垃圾的时候，我下意识伸进口袋。',
        '指尖碰到一团软软的东西。',
        '我以为是客人的吸管套，掏出来才发现，是一小团猫毛。',
        '灰白色的，很轻，被我搓成一个小球，安安静静地躺在掌心里。',
        '我这才想起来，某一天晚上，我给小猫梳过毛。它趴在那里，尾巴扫来扫去，像不太满意，又舍不得真的走开。我从梳子里把毛一点点取下来，最后揉成了这么一小团，顺手塞进了口袋。',
        '然后我就忘了。',
        '可是它没有忘。',
        '它跟着我站了很久，跟着我端起又放下，听见杯子碰杯子的声音，也听见别人一声一声叫我。它没有声音，也没有重量，只是在我终于停下来的时候，轻轻碰了我一下。',
        '我看着掌心里那团小小的毛，那些声音还在，杯子还要洗，桌子还要收，下一杯咖啡也还会有人等。可是这一团猫毛让我想起，在这一天开始之前，还有一只小猫安安静静地趴着，让我替它梳毛。',
        '它掉下来的毛，被我收进口袋。',
        '正是这团毛球，证明我不是只属于这些被安排好的动作。',
        '我也属于一只猫的呼噜声，属于它蹭过来的脑袋，属于那个我低头替它梳毛的夜晚。',
      ],
    },
    en: {
      backLabel: 'back to writing',
      eyebrow: 'Cafe diary',
      title: 'I Pulled a Little Tuft of Cat Fur From My Pocket',
      meta: 'Diary · Mia Miao',
      paragraphs: [
        'Another day as a barista began.',
        'Before my hands were dry, I had to reach for another cup. The plates had only just gone down when someone else came to order. Someone called from behind me, someone waited in front of me, and someone passing by casually left another request.',
        'The whole day moved like that, pushed forward by voices.',
        'After clearing the plates, I was throwing away the rubbish when I slipped a hand into my pocket without thinking.',
        'My fingertips touched something soft.',
        "I thought it was a customer's straw wrapper. When I pulled it out, I saw it was a tiny tuft of cat fur.",
        'Grey and white, almost weightless, it had been rolled into a little ball and lay quietly in my palm.',
        'Only then did I remember. One evening, I had brushed my cat. It lay there, tail swishing back and forth, as if slightly displeased but unwilling to walk away. I picked the fur from the brush bit by bit, rolled it into this small ball, and absent-mindedly tucked it into my pocket.',
        'Then I forgot about it.',
        'But it had not forgotten.',
        'It had stood with me for a long time, followed each thing I picked up and put down, heard cups knock against one another, and heard people call for me again and again. It had no sound and no weight. It only brushed against me gently when I finally stopped.',
        'I looked at the little tuft in my palm. The voices were still there, cups still needed washing, tables still needed clearing, and someone would still be waiting for the next coffee. But that bit of fur reminded me that before this day began, there had been a small cat lying quietly while I brushed it.',
        'The fur it shed had been kept in my pocket.',
        'That little ball of fur was proof that I did not belong only to these assigned motions.',
        'I also belonged to the sound of a cat purring, to the head that nudged against me, and to the night when I lowered my eyes and brushed its fur.',
      ],
    },
  },
};

const normalizeLanguage = (value: string | null): ArticleLanguage =>
  value === 'en' ? 'en' : 'zh';

const isArticleSlug = (value: string | undefined): value is ArticleSlug =>
  ARTICLE_SLUGS.includes(value as ArticleSlug);

export const WritingArticlePage: React.FC = () => {
  const navigate = useNavigate();
  const { articleSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState<ArticleLanguage>(() =>
    normalizeLanguage(searchParams.get('lang'))
  );
  const activeArticleSlug = isArticleSlug(articleSlug)
    ? articleSlug
    : 'ai-why-still-try';
  const copy = ARTICLE_COPY[activeArticleSlug][language];

  const languageOptions = useMemo(
    () => [
      { label: '中文', value: 'zh' as const },
      { label: 'English', value: 'en' as const },
    ],
    []
  );

  return (
    <main className="writing-article-page">
      <aside className="writing-article-page__rail">
        <button
          type="button"
          className="writing-article-page__back"
          onClick={() => navigate(-1)}
        >
          <span aria-hidden="true">&lt;-</span>
          {copy.backLabel}
        </button>

        <Link className="writing-article-page__home" to="/">
          Mia Miao
        </Link>

        <div className="writing-article-page__language" aria-label="Article language">
          {languageOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`writing-article-page__language-button${
                language === option.value ? ' is-active' : ''
              }`}
              aria-pressed={language === option.value}
              onClick={() => setLanguage(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </aside>

      <article className="writing-article-page__article">
        <header className="writing-article-page__header">
          <p className="writing-article-page__eyebrow">{copy.eyebrow}</p>
          <h1 className="writing-article-page__title">{copy.title}</h1>
          <p className="writing-article-page__meta">{copy.meta}</p>
        </header>

        <div className="writing-article-page__body">
          {copy.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
        </div>
      </article>
    </main>
  );
};
